import { UserProfile } from 'fitbeat.gym-core';
import { isLandscapeSelector } from 'fitbeat.react-native-components/HomeWorkouts';
import { IMetricResult } from 'fitbeat.models';
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { moderateScale } from 'react-native-size-matters';
import { connect } from 'react-redux';
import Theme from '../../theme';
import {
  CalorieIcon, HeartIcon, LoadIcon,
} from '../../ui/components/Icons';
import { toggleHeartRateMonitorModalVisible } from '../../ui/redux';
import { IAppStore } from '../reducer';
import {
  heartRateRecievedErrorSelector,
  liveHeartRateSourceTypeSelector,
  metricDataHasGoneStaleSelector,
  metricResultSelector,
} from '../selectors/heartRateMonitorSelectors';
import { ConnectHRMonitorButton, SetupHRMonitorButton, WaitingForHRMonitorButton } from './HeartRateMonitorButton';
import { Metric } from './Metric';

interface IProps {
  metricResult: IMetricResult | null;
  liveHeartRateSourceType: 'ConnectedApp' | 'Bluetooth' | null;
  userProfile: UserProfile;
  metricDataHasGoneStale: boolean;
  showSummary: boolean;
  hrMonitorError: string;
  t: any;
  isLandscape: boolean;
  workoutScreenTapped(): void;
  toggleHeartModal(visible: boolean): void;
}

export const getMetricsZoneColor = (zone: number): string => {
  const zoneMap: any = {
    0: 'rgb(255, 255, 255)',
    1: 'rgb(255, 255, 255)',
    2: 'rgb(13,104,234)',
    3: Theme.GREEN,
    4: 'rgb(255,98,29)',
    5: 'rgb(198,15,19)',
  };
  return zoneMap[zone];
};

export class MetricsRow extends React.PureComponent<IProps, any> {
  public render() {
    const {
      metricResult,
      liveHeartRateSourceType,
      userProfile,
      toggleHeartModal,
      workoutScreenTapped,
      metricDataHasGoneStale,
      showSummary,
      hrMonitorError,
      isLandscape,
      t,
    } = this.props;

    if (hrMonitorError) {
      return (
        <SetupHRMonitorButton onPress={() => toggleHeartModal(true)}/>
      );
    }

    const noHeartRateMonitorLinked = !userProfile?.heartRateMonitors?.home;
    const usingConnectedAppsForHR = liveHeartRateSourceType === 'ConnectedApp';
    if (noHeartRateMonitorLinked && !usingConnectedAppsForHR) {
      return (
        <ConnectHRMonitorButton onPress={() => {toggleHeartModal(true), workoutScreenTapped()}}/>
      );
    }

    if (!showSummary && (metricDataHasGoneStale || !metricResult)) {
      return (
        <WaitingForHRMonitorButton onPress={() => toggleHeartModal(true)}/>
      );
    }

    const metricsToShow = metricResult || {} as any;
    const heartRateZoneColour = showSummary
      ? Theme.WHITE
      : getMetricsZoneColor(metricsToShow.latestHeartRateZone || 0);

    return (
      <View style={{ flex: !isLandscape ? 1 : 0 }}>
        {
          showSummary && !isLandscape &&
          <View style={[styles.summaryHeader, { borderRightWidth: 0 }]}>
            <Text style={styles.summary}>WORKOUT SUMMARY</Text>
          </View>
        }
        <View style={styles.metricContainer}>
          {
            showSummary && isLandscape &&
            <View style={styles.summaryHeader}>
              <Text style={styles.summary}>WORKOUT</Text>
              <Text style={styles.summary}>SUMMARY</Text>
            </View>
          }
          <Metric
            value={metricsToShow.load}
            heartRateZoneColour={heartRateZoneColour}
            isLandscape={isLandscape}
            name={t('metricEffortPoints')}
            icon={<LoadIcon color={heartRateZoneColour} width={moderateScale(18)} height={moderateScale(18)}/>}
          />
          <Metric
            value={metricsToShow.calories}
            heartRateZoneColour={heartRateZoneColour}
            isLandscape={isLandscape}
            name={t('metricCalories')}
            icon={<CalorieIcon color={heartRateZoneColour} width={moderateScale(18)} height={moderateScale(18)}/>}
          />
          <Metric
            value={showSummary ? metricsToShow.averageHeartRate : metricsToShow.latestHeartRate}
            heartRateZoneColour={heartRateZoneColour}
            isLandscape={isLandscape}
            name={t('metricHeartRate')}
            icon={<HeartIcon color={heartRateZoneColour} size={moderateScale(20)}/>}
          />
          <Metric
            borderRight={false}
            value={showSummary ? metricsToShow.averageEffort : metricsToShow.latestEffort}
            heartRateZoneColour={heartRateZoneColour}
            isLandscape={isLandscape}
            name={t('metricHeartRatePercent')}
            icon={<Text style={[styles.effort, { color: heartRateZoneColour }]}>%</Text>}
          />
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  summaryHeader: {
    borderColor: Theme.GREY_MEDIUM,
    borderRightWidth: 1,
    paddingHorizontal: moderateScale(12),
    padding: moderateScale(5),
  },
  summary: {
    fontSize: moderateScale(15, 0.7),
    fontFamily: Theme.Custom700,
    textAlign: 'center',
    color: Theme.WHITE,
  },
  metricContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  effort: {
    fontSize: moderateScale(20),
    lineHeight: moderateScale(20),
    color: Theme.WHITE,
  },
});

const mapStateToProps = (state: IAppStore) => ({
  metricResult: metricResultSelector(state),
  liveHeartRateSourceType: liveHeartRateSourceTypeSelector(state),
  hrMonitorError: heartRateRecievedErrorSelector(state),
  metricDataHasGoneStale: metricDataHasGoneStaleSelector(state),
  showSummary: state.homeWorkouts.inOrPastWarmdown,
  isLandscape: isLandscapeSelector(state),
});

const mapDispatchToProps = (dispatch: any, ownProps: IProps) => {
  return {
    toggleHeartModal: async (visible: boolean) => {
      dispatch(toggleHeartRateMonitorModalVisible(visible));
    },
  };
};

export const Metrics = connect(mapStateToProps, mapDispatchToProps)(MetricsRow);
